import { useQuery } from "@tanstack/react-query"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import getPodcastFromFeed from "podparse"

import { useFlipperFeatureEnabled } from "source/shared/flipperFeatures"

const CACHE_TIME = 5 * 60 * 1000 // 5 minutes

async function fetchPodcastFeedFromProxy(pageId: string, blockId: string) {
  const response = (await sessionApiClient.get(
    `/publishing/v2/pages/${pageId}/external_podcasts/${blockId}`,
  )) as {
    data: { attributes: { status: number; content_type: string; body: string } }
  }
  const { status, body } = response.data.attributes

  if (status < 200 || status >= 300)
    throw new Error(`Failed to fetch podcast feed: ${status}`)

  return body
}

async function fetchPodcastFeedDirectly(feedUrl: string) {
  const parsedUrl = new URL(feedUrl)
  if (!["http:", "https:"].includes(parsedUrl.protocol))
    throw new Error("Invalid URL protocol. Only HTTP and HTTPS are allowed.")

  const response = await fetch(feedUrl)
  if (!response.ok)
    throw new Error(
      `Failed to fetch podcast feed: ${response.status} ${response.statusText}`,
    )

  return response.text()
}

function parsePodcastFeed(xmlText: string) {
  try {
    const podcast = getPodcastFromFeed(xmlText)

    return {
      title: podcast.meta.title,
      description: podcast.meta.description,
      artwork: podcast.meta.image?.url,
      episodes: podcast.episodes
        .map((episode) => ({
          title: episode.title,
          description: episode.description,
          audioUrl: episode.enclosure?.url,
          pubDate: episode.pubDate,
          duration: episode.duration,
          artwork: episode.image?.url || podcast.meta.image?.url,
        }))
        .filter((episode) => !!episode.audioUrl),
    }
  } catch (error) {
    console.error("Error parsing podcast feed:", error)
    throw error
  }
}

export default function usePodcastFeed(
  pageId: string,
  blockId: string,
  feedUrl: string,
) {
  const proxyEnabled = useFlipperFeatureEnabled("ROLLOUT_podcast_proxy")
  const internalHost = [
    "publishing.pco.test",
    "publishing-staging.planningcenteronline.com",
    "publishing.planningcenteronline.com",
  ]

  const isInternalFeed = internalHost.some((host) => feedUrl.includes(host))
  const shouldUseProxy = proxyEnabled && !isInternalFeed

  return useQuery({
    queryKey: ["podcast", pageId, blockId, feedUrl],
    queryFn: () =>
      shouldUseProxy
        ? fetchPodcastFeedFromProxy(pageId, blockId)
        : fetchPodcastFeedDirectly(feedUrl),
    enabled: !!feedUrl && !!pageId && !!blockId,
    staleTime: CACHE_TIME,
    select: (xmlText) => parsePodcastFeed(xmlText),
  })
}
